import React from "react";
import Functions from "../../Common/Functions";
import { havePermission, printLabel } from "../../../permissions";
import Constant, { selectedPolygonTypes } from "../../Common/Constants";
import fetchData from "../../Common/FetchDataFunctions";
import LAYER_PERMISSIONS from "../../../permissions/Layers.json";
import { Button, Popover } from "antd";
import { isEqual } from "lodash";

const keyMapping = {
	[selectedPolygonTypes.PopulationGrids]: "Population",
	[`${selectedPolygonTypes.DailyTrafficData}-trafficData`]: "Traffic",
	[`${selectedPolygonTypes.DailyTrafficData}-weeklyData`]: "Days",
	[`${selectedPolygonTypes.DailyTrafficData}-hourlyData`]: "Hours",
	[selectedPolygonTypes.sec_density]: "SEC",
	[selectedPolygonTypes.whiteSpace_density]: "White Space"
};

const valuesMapping = {
	Days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
	Hours: [
		"12 AM",
		"1 AM",
		"2 AM",
		"3 AM",
		"4 AM",
		"5 AM",
		"6 AM",
		"7 AM",
		"8 AM",
		"9 AM",
		"10 AM",
		"11 AM",
		"12 PM",
		"1 PM",
		"2 PM",
		"3 PM",
		"4 PM",
		"5 PM",
		"6 PM",
		"7 PM",
		"8 PM",
		"9 PM",
		"10 PM",
		"11 PM"
	]
};
const { fetchExtraData } = fetchData;
const { getBooleanVale } = Functions;
const { HomeActions: Actions } = Constant;
const { swtichPolygons } = Actions;

const getPolygonFilterValues = ({ filters, polygonType, defaultFilterValues }) => {
	return Object.keys(filters)
		.filter(
			key =>
				key.startsWith(polygonType) &&
				filters[key] !== undefined &&
				!isEqual(filters[key], defaultFilterValues[key])
		)
		.map(key => {
			// Map the key to a label if one exists, otherwise use the original key.
			const keyNameMapping = keyMapping[key] || key;

			// If the filter value is an array, treat the first element as the min and the second as the max.
			if (Array.isArray(filters[key])) {
				const [minIndex, maxIndex] = filters[key];

				// Look up the corresponding values from valuesMapping if available.
				const mappedArray = valuesMapping[keyNameMapping];
				const mappedMin = mappedArray?.[minIndex] ?? minIndex;
				const mappedMax = mappedArray?.[maxIndex] ?? maxIndex;

				return `${keyNameMapping}: Min: ${mappedMin}  Max: ${mappedMax}`;
			}

			// If the filter is not an array, just return the value.
			return `${keyNameMapping}: ${filters[key]}`;
		});
};

const PolygonButton = ({
	permission,
	loading,
	selectedPolygons,
	polygonType,
	label,
	geo,
	tenant,
	dispatch,
	extraDataKeys = [],
	filters,
	pop_info
}) => {
	// Ensure permissions are checked correctly
	if (!permission || !havePermission(permission)) return null;

	const isSelected = selectedPolygons === polygonType;

	const handleClick = async () => {
		if (loading === false && loading !== undefined) {
			dispatch({ type: swtichPolygons, polygons: polygonType });
		} else {
			try {
				for (const key of extraDataKeys) {
					await fetchExtraData(geo, tenant, dispatch, key, polygonType);
				}
				dispatch({ type: swtichPolygons, polygons: polygonType });
			} catch (error) {
				console.error(`Error fetching data for ${label}:`, error);
			}
		}
	};

	if (filters.length > 0) {
		return (
			<Popover
				content={
					<div>
						<p>{label} Filters</p>
						{filters.map((filter, index) => (
							<p key={index}>{filter}</p>
						))}
					</div>
				}
				trigger="hover"
			>
				<Button
					loading={loading}
					className="togle-buttons-map mr-2"
					style={{
						// border: "0.12rem solid gray",
						borderColor: "gray",
						backgroundColor: "#33a0d5",
						color: "#FFFFFF",
						boxShadow:
							"rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px"
					}}
					onClick={handleClick}
				>
					{printLabel(loading, label)}
				</Button>
			</Popover>
		);
	}

	return pop_info ? (
		<Popover
			content={
				<div
					style={{
						width: "250px",
						overflowY: "auto",
						borderRadius: "8px"
					}}
				>
					<span style={{ color: "#85929e" }}>{pop_info}</span>
				</div>
			}
			trigger="hover"
		>
			<Button
				loading={loading}
				className="togle-buttons-map mr-2 mb-2"
				style={{
					borderColor: "#33a0d5",
					backgroundColor: isSelected ? "#33a0d5" : "",
					color: isSelected ? "#FFFFFF" : "#33a0d5",
					boxShadow: loading ? "0px 3px 8px rgba(0, 0, 0, 0.24)" : "none"
				}}
				onClick={handleClick}
			>
				{printLabel(loading, label)}
			</Button>
		</Popover>
	) : (
		<Button
			loading={loading}
			className="togle-buttons-map mr-2 mb-2"
			style={{
				borderColor: "#33a0d5",
				backgroundColor: isSelected ? "#33a0d5" : "",
				color: isSelected ? "#FFFFFF" : "#33a0d5",
				boxShadow: loading ? "0px 3px 8px rgba(0, 0, 0, 0.24)" : "none"
			}}
			onClick={handleClick}
		>
			{printLabel(loading, label)}
		</Button>
	);
};

const PolygonLayers = ({ dispatch, tenant, selectedPolygons, loadingsData, geo, filters, defaultFilterValues }) => {
	const polygonTypes = selectedPolygonTypes;

	const getLoadingState = type => getBooleanVale(loadingsData, `${geo}-${type}`, undefined);

	const buttonsConfig = [
		{
			permission: LAYER_PERMISSIONS.LAYER_CELL_SITE,
			loading: getLoadingState(polygonTypes.CELLSITES),
			polygonType: polygonTypes.CELLSITES,
			label: "Cell Sites",
			extraDataKeys: ["cell_tower_data", "cell_tower_mapping"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_COMPARISON_WHITE_SPACE,
			loading: getLoadingState(polygonTypes.SAWhiteSpace),
			polygonType: polygonTypes.SAWhiteSpace,
			label: "Comparison",
			extraDataKeys: ["whitespace_data"],
			pop_info:
				"Reynold 's own numeric distribution compared to overall numeric distribution potential based on digital census"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_WHITE_SPACE,
			loading: getLoadingState(polygonTypes.whiteSpace),
			polygonType: polygonTypes.whiteSpace,
			label: "White Space",
			extraDataKeys: ["whitespace_data"],
			pop_info: "Highlights areas where population weighted outlets exist, but Reynolds has no coverage."
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_SHOPPER_PROFILE,
			loading: getLoadingState(polygonTypes.SecPolgons),
			polygonType: polygonTypes.SecPolgons,
			label: "Affluence",
			extraDataKeys: ["sec_polygons"],
			pop_info:
				"The color coding on the map shows neighborhoods based on consumer socioeconomic class verified with government sourced income levels"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_DENSTIY_POPULATION,
			loading: getLoadingState(polygonTypes.populationDensity),
			polygonType: polygonTypes.populationDensity,
			label: "Population Density",
			extraDataKeys: ["population_density"],
			pop_info:
				"Population density map is a heat map where the colored region shows the density of population. Darker the color, denser the population"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_SHOP_DESNTIY,
			loading: getLoadingState(polygonTypes.shopDensity),
			polygonType: polygonTypes.shopDensity,
			label: "Shop Density",
			extraDataKeys: ["shop_density"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_GRIDS_POPULATION,
			loading: getLoadingState(polygonTypes.PopulationGrids),
			polygonType: polygonTypes.PopulationGrids,
			label: "Population Grids",
			extraDataKeys: ["population_density"],
			pop_info:
				"Population data collected at 100 sq meter granularity , aggregated within 1-kilometer square grid"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_SEC_DENSITY,
			loading: getLoadingState(polygonTypes.sec_density),
			polygonType: polygonTypes.sec_density,
			label: "Affluence Grids",
			extraDataKeys: ["population_density"],
			pop_info:
				"SEC density map is a heat map where the colored region shows the predominance of a socio-economic class at 1 kilometer square granularity "
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_DAILY_TRAFFIC_DATA,
			loading: getLoadingState(polygonTypes.DailyTrafficData),
			polygonType: polygonTypes.DailyTrafficData,
			label: "Daily Traffic Data",
			extraDataKeys: ["population_density"],
			pop_info:
				"Visits in the store (system tracks at the accuracy of 100 sq meter, and estimates the store traffic data)"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_WHITE_SPACE_DENSITY,
			loading: getLoadingState(polygonTypes.whiteSpace_density),
			polygonType: polygonTypes.whiteSpace_density,
			label: "White Space Grids",
			extraDataKeys: ["population_density"],
			pop_info:
				"Highlights 1 kilometer grid boxes where population weighted outlets exist, but Reynolds has no coverage"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_DEMOGRAPHIC_POPULATION_DENSITY,
			loading: getLoadingState(polygonTypes.demographicPopulationDensity),
			polygonType: polygonTypes.demographicPopulationDensity,
			label: "Consumer Profile",
			extraDataKeys: ["population_density"],
			pop_info: "Consumer age profile, gender, and education"
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_LAND_TYPE,
			loading: getLoadingState(polygonTypes.AreaPolygons),
			polygonType: polygonTypes.AreaPolygons,
			label: "Land Type Grids",
			extraDataKeys: ["area_polygons"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_URBANIZATION,
			loading: getLoadingState(polygonTypes.UrbanizationPolygons),
			polygonType: polygonTypes.UrbanizationPolygons,
			label: "Urbanization Polygons",
			extraDataKeys: ["urban_rural_polygons"]
		},

		{
			permission: LAYER_PERMISSIONS.LAYER_DIGITAL_PERSONA,
			loading: getLoadingState(polygonTypes.digitalPersona),
			polygonType: polygonTypes.digitalPersona,
			label: "Digital Persona",
			extraDataKeys: ["population_density"]
		},

		{
			permission: LAYER_PERMISSIONS.LAYER_ADDITIONAL_LAYER,
			loading: getLoadingState(polygonTypes.additional_shops),
			polygonType: polygonTypes.additional_shops,
			label: "Additional Shops",
			extraDataKeys: ["whitespace_data"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_MOTOR_SPORTS,
			loading: getLoadingState(polygonTypes.motor_spots),
			polygonType: polygonTypes.motor_spots,
			label: "Motor Sports Enthusiasts",
			extraDataKeys: ["population_density"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_SOCIAL_MEDIA_BEHAVIOURS,
			loading: getLoadingState(polygonTypes.social_media_behaviors),
			polygonType: polygonTypes.social_media_behaviors,
			label: "Digital Persona",
			extraDataKeys: ["population_density"]
		},
		{
			permission: LAYER_PERMISSIONS.LAYER_SMOKING_ZONES,
			loading: getLoadingState(polygonTypes.smoking_zones),
			polygonType: polygonTypes.smoking_zones,
			label: "Smoking Zones",
			extraDataKeys: ["whitespace_data"]
		}
	];

	return (
		<>
			{buttonsConfig.map((config, index) => (
				<PolygonButton
					filters={getPolygonFilterValues({
						filters,
						polygonType: config.polygonType,
						defaultFilterValues
					})}
					key={index}
					dispatch={dispatch}
					tenant={tenant}
					selectedPolygons={selectedPolygons}
					geo={geo}
					{...config}
				/>
			))}
		</>
	);
};

export default PolygonLayers;
